module EEX.McEc.Controls.CertificateDialog {

    // ReSharper disable once InconsistentNaming
    export interface CertificateDialogBaseAttribute extends AppKitchen.ModelBaseAttributes {
        canContinue?: boolean,
        canComplete?: boolean,
    }

    export abstract class CertificateDialogBaseModel<TAttributes extends CertificateDialogBaseAttribute> extends AppKitchen.ModelBase<TAttributes> {

        protected model: CertificateDialogModel;

        private processStep: number;

        constructor(model: CertificateDialogModel, processStep: number, attributes?: TAttributes) {
            super(attributes);

            this.model = model;
            this.processStep = processStep;
        }

        public isCanContinue(): boolean {
            return this.get().canContinue;
        }

        public canContinue(): void {
            this.set({ canContinue: true });
        }

        public canNotContinue(): void {
            this.set({ canContinue: false });
        }

        public isCanComplete(): boolean {
            return this.get().canComplete;
        }

        public canComplete(): void {
            this.set({ canComplete: true });
        }

        public canNotComplete(): void {
            this.set({ canComplete: false });
        }

        public getCurrentProcessStep(): number {
            return this.model.get().currentProcessStep;
        }

        public getTotalProcessSteps(): number {
            return this.model.get().totalProcessSteps;
        }

        public updateCurrentProcessStep(): void {
            this.model.set({ currentProcessStep: this.processStep });
        }
    }

    // ReSharper disable once InconsistentNaming
    export interface CertificateDialogBaseOptions extends AppKitchen.ViewBaseOptions {
        title: string;
    }

    export abstract class CertificateDialogBaseView<TModel extends CertificateDialogBaseModel<CertificateDialogBaseAttribute>>
    extends AppKitchen.ViewBase<CertificateDialogBaseModel<CertificateDialogBaseAttribute>> {
        options: CertificateDialogBaseOptions;

        private prevCallback: () => void;
        private nextCallback: () => void;
        private finishCallback: () => void;
        private nextCallbackWithoutClick: boolean;

        private contentTemplate: string;

        private containersBase: {
            content: HTMLElement,
        }

        private buttonsBase: {
            prev: JQuery,
            next: JQuery,
            finish: JQuery,
        }

        constructor(model: TModel, target: HTMLElement, contentTemplate: string, options?: CertificateDialogBaseOptions) {
            super(model,
                target,
                options);

            this.setTemplate(McEc.Templates.CertificateDialog_Base);
            this.contentTemplate = contentTemplate;
        }
        
        public render() {
            this.model.updateCurrentProcessStep();
            this.renderTemplate({
                title: this.options.title,
                currentProcessStep: this.model.getCurrentProcessStep(),
                totalProcessSteps: this.model.getTotalProcessSteps()
            });
            this.setContainersBase();
            this.renderContentTemplate();
            this.setButtonsBase();
            this.setButtonActionsBase();
            AppKitchen.UIHelper.updateFullHeightGrids();
            return this;
        }

        private setContainersBase() {
            this.containersBase = {
                content: this.$el.find(".eex-mcec-certificate-dialog-base-content-wrapper")[0]
            }
        }

        private renderContentTemplate() {
            AppKitchen.UIHelper.renderTemplateTo(this.containersBase.content, this.contentTemplate);
        }

        private setButtonsBase() {
            this.buttonsBase = {
                prev: this.$el.find(".eex-mcec-certificate-dialog-prev-btn"),
                next: this.$el.find(".eex-mcec-certificate-dialog-next-btn"),
                finish: this.$el.find(".eex-mcec-certificate-dialog-finish-btn"),
            }
        }

        private setButtonActionsBase() {
            this.setButtonBehavior(this.buttonsBase.prev, this.prevCallback);
            this.setButtonBehaviorOnChange(this.buttonsBase.next, this.nextCallback, "canContinue", this.nextCallbackWithoutClick);
            this.setButtonBehaviorOnChange(this.buttonsBase.finish, this.finishCallback, "canComplete", false);
        }

        protected toggleContentDisable(e: JQuery, show: boolean) {
            if (show) {
                e.removeClass("disabled");
            } else {
                e.addClass("disabled");
            }
        }

        protected toggleContentVisibility(e: JQuery, show: boolean) {
            if (show) {
                e.show();
            } else {
                e.hide();
            }
        }

        private setButtonBehaviorOnChange(button: JQuery, callback: () => void, action: string, withoutClick: boolean) {
            if (callback) {
                if (withoutClick) {
                    button.hide();
                    this.model.on(`change:${action}`,
                        () => {
                            callback();
                        });
                } else {
                    button.click(() => {
                        if (this.getActionStatus(action)) {
                            callback();
                        }
                    });

                    this.model.on(`change:${action}`,
                        () => {
                            this.toggleContentDisable(button, this.getActionStatus(action));
                        });
                    this.toggleContentDisable(button, this.getActionStatus(action));
                }
            } else {
                button.hide();
            }
        }

        private getActionStatus(action: string): boolean {
            const isActive = this.model.get()[action];
            if (isActive) {
                return true;
            }
            return false;
        }

        private setButtonBehavior(button: JQuery, callback: () => void): void {
            if (callback) {
                button.click(() => {
                    callback();
                });
            } else {
                button.hide();
            }
        }

        public prev(prev: () => void): void {
            this.prevCallback = prev;
        }

        public next(next: () => void, withoutClick?: boolean): void {
            this.nextCallback = next;
            this.nextCallbackWithoutClick = withoutClick ? withoutClick : false;
        }

        public finish(finish: () => void): void {
            this.finishCallback = finish;
        }
    }
}
